import { RiFireFill, RiPrinterFill } from '@remixicon/react'
import { format, parseISO } from 'date-fns'
import React, { useRef, useState } from 'react'
import QRCode from 'react-qr-code'
import { useReactToPrint } from 'react-to-print'

import { useFlows } from '@/api/flows/flows'
import type { Detail, StandardTimesKeys } from '@/api/orders/orders-types'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { NumberInput } from '@/components/ui/number-input'
import { Separator } from '@/components/ui/separator'
import { DATE_FORMATS } from '@/constants/app'
import { cn } from '@/lib/utils'
import { usePrintStickers } from '@/store/print-stickers'
import { getNextFlowAfter } from '@/utils/details'

// const getIdStickerLink = (detail: Detail) => {
//   const log = detail?.production_logs?.at(-1)
//   const baseUrl = window.location.origin

//   let link = baseUrl

//   if (detail?.production_logs.length >= 2 && log?.stage_id) {
//     link += `/machines/${log.stage_id}/?detail=${detail.autoid}`
//   } else {
//     link += `/?tab=items&detail=${detail.autoid}&search=${detail.invoice}`
//   }

//   if (log?.production_date) {
//     link += `&date=${format(parseISO(log.production_date), DATE_FORMATS.iso)}`
//   }

//   return link
// }

const getIdStickerLink = (detail: Detail) => `${window.location.origin}/scanner/${detail.autoid}`

const PrintableIdStickerCard = React.forwardRef<
  HTMLDivElement,
  {
    detail: Detail
    inven: StandardTimesKeys | undefined
    stickerIndex: number
    totalStickers: number
  }
>(({ detail, inven, stickerIndex, totalStickers }, ref) => {
  const { data: flows } = useFlows()

  const nextFlow = getNextFlowAfter({ detail, flows: flows?.results, afterInven: inven })

  const maxStartTime = nextFlow ? detail?.min_start_times?.[nextFlow.inven] : null

  const link = getIdStickerLink(detail)

  return (
    <Card
      ref={ref}
      className='printable-card mx-auto gap-2.5 rounded-none border-none p-2.5 font-medium'
      style={{ width: '4in', height: '2.5in', boxSizing: 'border-box' }}
    >
      <div className='flex h-8 items-start gap-2.5'>
        <div className='flex flex-col gap-0.5'>
          <div className='text-black-200 text-[10px] leading-none'>Customer</div>
          <div className='text-[11px]'>{detail?.customer ?? '-'}</div>
        </div>

        <Separator
          className='h-5'
          orientation='vertical'
        />
        <div className='flex flex-col gap-0.5'>
          <div className='text-black-200 text-[10px] leading-none'>Ship tag</div>
          <div className='text-[11px]'>{detail?.ship_tag ?? '-'}</div>
        </div>

        <div className='border-black-200 ml-auto flex h-4 items-center justify-center self-start rounded-sm border p-1 text-[10px]'>
          {stickerIndex} of {totalStickers}
        </div>
      </div>

      <div className='flex h-full items-start justify-between gap-2.5'>
        <div className='size-full space-y-3'>
          <div className='flex h-6.5 items-center gap-2.5'>
            <div className='flex flex-col gap-0.5'>
              <div className='text-black-200 text-[10px] leading-none'>Order</div>
              <div className='text-[11px]'>#{detail?.invoice ?? '-'}</div>
            </div>

            <Separator
              className='h-5'
              orientation='vertical'
            />

            <div className='flex flex-col gap-0.5'>
              <div className='text-black-200 text-[10px] leading-none'>Product</div>
              <div className='text-[11px]'>{detail?.inven}</div>
            </div>

            <Separator
              className='h-5'
              orientation='vertical'
            />

            <div className='flex flex-col gap-0.5'>
              <div className='text-black-200 text-[10px] leading-none'>Qty</div>
              <div className='text-[11px]'>{detail?.quan}</div>
            </div>

            <Separator
              className='h-5'
              orientation='vertical'
            />

            <div className='flex flex-col gap-0.5'>
              <div className='text-black-200 text-[10px] leading-none'>Color</div>
              <div className='text-[11px]'>{detail?.color ?? '-'}</div>
            </div>
          </div>

          <div className='flex w-full items-center gap-2.5 whitespace-nowrap'>
            <div className='flex flex-col gap-0.5'>
              <div className='text-black-200 text-[10px] leading-none'>Max start time</div>
              <div className='text-[11px]'>
                {maxStartTime ? format(new Date(maxStartTime), DATE_FORMATS.shortDateWithTime) : '-'}
              </div>
            </div>

            <Separator
              className='h-5'
              orientation='vertical'
            />

            <div className='flex flex-col gap-0.5'>
              <div className='text-black-200 text-[10px] leading-none'>Next process</div>
              <div className='text-[11px]'>{nextFlow?.name ?? 'No next process'}</div>
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <div className='flex max-w-60 flex-col gap-0.5'>
              <div className='text-black-200 text-[10px] leading-none'>Blank size</div>
              <div className='text-base'>
                {detail.width || '-'} x{' '}
                <span className={cn(detail.heightd !== 120 ? 'font-bold' : '')}>{detail.heightd}</span>
              </div>
            </div>

            <Separator
              className='h-5'
              orientation='vertical'
            />

            <div className='flex flex-col gap-0.5'>
              <div className='text-black-200 text-[10px] leading-none'>Shipping</div>
              <div className='flex items-center gap-2'>
                <span className='text-[11px]'>
                  {detail.ship_date ? format(parseISO(detail.ship_date), DATE_FORMATS.dots) : '-'} {detail?.ship_time}
                </span>
                <Separator
                  className='h-3'
                  orientation='vertical'
                />
                <span
                  className={cn(
                    'flex items-center gap-0.5 rounded-sm',
                    detail?.ship_type === 'SET OUTSIDE'
                      ? 'text-white-100 bg-black-200 py-0.5 pr-1 pl-0.5 text-[10px] leading-none'
                      : 'text-[10px]'
                  )}
                >
                  {detail?.ship_type === 'SET OUTSIDE' ? <RiFireFill className='-mt-px size-2.5' /> : null}

                  <span>{detail?.ship_type ?? '-'}</span>
                </span>
              </div>
            </div>
          </div>

          <div className='flex max-w-60 flex-col gap-0.5'>
            <div className='text-black-200 text-[10px] leading-none'>Description</div>
            <div className='mt-auto text-[10px]'>{detail?.descr ?? '-'}</div>
          </div>
        </div>
        <div className='mt-auto size-24'>
          <QRCode
            onClick={() => window.open(link)}
            className='mt-2.5 size-full rounded-sm'
            value={link}
          />
        </div>
      </div>
    </Card>
  )
})

const PrintableIdStickerWrapper = React.forwardRef<
  HTMLDivElement,
  { detail: Detail; inven: StandardTimesKeys | undefined; totalStickers: number }
>(({ detail, inven, totalStickers }, ref) => {
  const stickerArray = Array.from({ length: totalStickers })

  return (
    <div ref={ref}>
      {stickerArray.map((_, index) => (
        <React.Fragment key={`id-sticker-${index}`}>
          <PrintableIdStickerCard
            detail={detail}
            inven={inven}
            stickerIndex={index + 1}
            totalStickers={totalStickers}
          />
          {index < totalStickers - 1 && <div className='page-break-after' />}
        </React.Fragment>
      ))}
    </div>
  )
})

export const IdStickers = ({ detail, inven }: { detail: Detail; inven: StandardTimesKeys | undefined }) => {
  const [stickersToPrint, setStickersToPrint] = useState(1)

  const { data: flows } = useFlows()

  const nextFlow = getNextFlowAfter({ detail, flows: flows?.results, afterInven: inven })

  const maxStartTime = nextFlow ? detail?.min_start_times?.[nextFlow.inven] : null

  const link = getIdStickerLink(detail)
  const printContentRef = useRef<HTMLDivElement>(null)

  const stickerPageStyle = `
    @page {
      size: 4in 2.5in;
      margin: 0;
    }

    @media print {
      html, body {
        margin: 0;
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
      }

      /* Printable card exact size and padding for paper */
      .printable-card {
        width: 4in !important;
        height: 2.5in !important;
        box-sizing: border-box !important;
        padding: 0.75rem !important; /* match p-3 */
        margin: 0 auto !important;
        border: none !important;
        overflow: hidden !important;
        background: #fff !important;
      }

      /* Page break after each sticker */
      .page-break-after {
        page-break-after: always;
        break-after: page;
        height: 0 !important;
        overflow: hidden !important;
      }

      /* QR code size for print (1in square) */
      .size-\\[220px\\] {
        width: 1in !important;
        height: 1in !important;
      }

       .leading-none { line-height: 1 !important; }

      /* Text size tweaks */
      .text-\\[11px\\] { font-size: 8pt !important; line-height: 1 !important; }
      .text-[11px] { font-size: 10pt !important; line-height: 1.1 !important; }

      /* Reduce layout gaps for print (use absolute units) */
      .gap-2 { gap: 0.2cm !important; }
      .gap-2\\.5 { gap: 0.25cm !important; }
      .gap-0\\.5 { gap: 0.05cm !important; }
      .space-y-2.5 > *:not(:last-child) { margin-bottom: 0.4cm !important; }
      .mt-2 { margin-top: 0.2cm !important; }

      /* Hide interactive UI */
      .no-print { display: none !important; }
    }
  `

  const { setPrintDetail, setPrintOrder } = usePrintStickers()

  const reactToPrintFn = useReactToPrint({
    contentRef: printContentRef,
    documentTitle: `ID Stickers - ${detail?.invoice}`,
    pageStyle: stickerPageStyle,
    onBeforePrint: async () => {
      setTimeout(() => {
        setPrintDetail(null)
        setPrintOrder(null)
      }, 200)
    }
  })

  return (
    <div className='w-full'>
      <Card
        className='no-print mx-auto gap-2.5 rounded-md border p-2.5 font-medium'
        style={{ width: '4in', height: '2.5in', boxSizing: 'border-box' }}
      >
        <div className='flex h-8 items-start gap-2.5'>
          <div className='flex flex-col gap-0.5'>
            <div className='text-black-200 text-[10px] leading-none'>Customer</div>
            <div className='text-[11px]'>{detail?.customer ?? '-'}</div>
          </div>

          <Separator
            className='h-5'
            orientation='vertical'
          />
          <div className='flex flex-col gap-0.5'>
            <div className='text-black-200 text-[10px] leading-none'>Ship tag</div>
            <div className='text-[11px]'>{detail?.ship_tag ?? '-'}</div>
          </div>

          <div className='border-black-200 ml-auto flex h-4 items-center justify-center self-start rounded-sm border p-1 text-[10px]'>
            1 of {stickersToPrint}
          </div>
        </div>

        <div className='flex h-full items-start justify-between gap-2.5'>
          <div className='size-full space-y-3'>
            <div className='flex h-6.5 items-center gap-2.5'>
              <div className='flex flex-col gap-0.5'>
                <div className='text-black-200 text-[10px] leading-none'>Order</div>
                <div className='text-[11px]'>#{detail?.invoice ?? '-'}</div>
              </div>

              <Separator
                className='h-5'
                orientation='vertical'
              />

              <div className='flex flex-col gap-0.5'>
                <div className='text-black-200 text-[10px] leading-none'>Product</div>
                <div className='text-[11px]'>{detail?.inven}</div>
              </div>

              <Separator
                className='h-5'
                orientation='vertical'
              />

              <div className='flex flex-col gap-0.5'>
                <div className='text-black-200 text-[10px] leading-none'>Qty</div>
                <div className='text-[11px]'>{detail?.quan}</div>
              </div>

              <Separator
                className='h-5'
                orientation='vertical'
              />

              <div className='flex flex-col gap-0.5'>
                <div className='text-black-200 text-[10px] leading-none'>Color</div>
                <div className='text-[11px]'>{detail?.color ?? '-'}</div>
              </div>
            </div>

            <div className='flex w-full items-center gap-2.5 whitespace-nowrap'>
              <div className='flex flex-col gap-0.5'>
                <div className='text-black-200 text-[10px] leading-none'>Max start time</div>
                <div className='text-[11px]'>
                  {maxStartTime ? format(new Date(maxStartTime), DATE_FORMATS.shortDateWithTime) : '-'}
                </div>
              </div>

              <Separator
                className='h-5'
                orientation='vertical'
              />

              <div className='flex flex-col gap-0.5'>
                <div className='text-black-200 text-[10px] leading-none'>Next process</div>
                <div className='text-[11px]'>{nextFlow?.name ?? 'No next process'}</div>
              </div>
            </div>

            <div className='flex items-center gap-2'>
              <div className='flex max-w-60 flex-col gap-0.5'>
                <div className='text-black-200 text-[10px] leading-none'>Blank size</div>
                <div className='text-base'>
                  {detail.width || '-'} x{' '}
                  <span className={cn(detail.heightd !== 120 ? 'font-bold' : '')}>{detail.heightd}</span>
                </div>
              </div>

              <Separator
                className='h-5'
                orientation='vertical'
              />

              <div className='flex flex-col gap-0.5'>
                <div className='text-black-200 text-[10px] leading-none'>Shipping</div>
                <div className='flex items-center gap-2'>
                  <span className='text-[11px]'>
                    {detail.ship_date ? format(parseISO(detail.ship_date), DATE_FORMATS.dots) : '-'} {detail?.ship_time}
                  </span>
                  <Separator
                    className='h-3'
                    orientation='vertical'
                  />
                  <span
                    className={cn(
                      'flex items-center gap-0.5 rounded-sm',
                      detail?.ship_type === 'SET OUTSIDE'
                        ? 'text-white-100 bg-black-200 py-0.5 pr-1 pl-0.5 text-[10px] leading-none'
                        : 'text-[10px]'
                    )}
                  >
                    {detail?.ship_type === 'SET OUTSIDE' ? <RiFireFill className='-mt-px size-2.5' /> : null}

                    <span>{detail?.ship_type ?? '-'}</span>
                  </span>
                </div>
              </div>
            </div>

            <div className='flex max-w-60 flex-col gap-0.5'>
              <div className='text-black-200 text-[10px] leading-none'>Description</div>
              <div className='mt-auto text-[10px]'>{detail?.descr ?? '-'}</div>
            </div>
          </div>
          <div className='mt-auto size-24'>
            <QRCode
              onClick={() => window.open(link)}
              className='mt-2.5 size-full rounded-sm'
              value={link}
            />
          </div>
        </div>
      </Card>

      <div style={{ position: 'absolute', left: '-99910px', top: 0 }}>
        <PrintableIdStickerWrapper
          ref={printContentRef}
          detail={detail}
          inven={inven}
          totalStickers={stickersToPrint}
        />
      </div>

      <div className='mt-4 flex w-full items-end gap-2.5'>
        <div className='flex flex-1 flex-col gap-2.5'>
          <div className='text-black-200 text-[11px]'>Stickers to print</div>
          <NumberInput
            className='[&_input]:bg-white-100 [&_button]:bg-white-100 [&_button]:hover:!bg-white-100'
            onChange={setStickersToPrint}
            value={stickersToPrint}
            minValue={1}
          />
        </div>

        <Button
          className='flex-1'
          onClick={reactToPrintFn}
        >
          <RiPrinterFill />
          <span>Print {stickersToPrint} sticker(s)</span>
        </Button>
      </div>
    </div>
  )
}
